import { useState } from 'react';
import { Box, Stepper, Step, StepLabel } from '@mui/material';
import SchoolSelectionStep from './ScheduleTour/SchoolSelectionStep';
import TimePickerStep from './ScheduleTour/TimePickerStep';
import ContactInfoStep from './ScheduleTour/ContactInfoStep';
import ConfirmationStep from './ScheduleTour/ConfirmationStep';

const steps = ['Select School', 'Pick a Time', 'Your Information', 'Confirmation'];

export interface TourFormData {
    school: string;
    schoolName: string;
    schoolAddress1?: string;
    schoolAddress2?: string;
    tourDate: string;
    tourTime: string;
    childFirstName: string;
    childLastName: string;
    birthday: string;
    parentFirstName: string;
    parentLastName: string;
    phoneNumber: string;
    email: string;
    message: string;
    gradeInterest?: string;
}

function ScheduleTourFlow() {
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState<TourFormData>({
        school: '',
        schoolName: '',
        tourDate: '',
        tourTime: '',
        childFirstName: '',
        childLastName: '',
        birthday: '',
        parentFirstName: '',
        parentLastName: '',
        phoneNumber: '',
        email: '',
        message: '',
    });

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const updateFormData = (data: Partial<TourFormData>) => {
        setFormData((prev) => ({ ...prev, ...data }));
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <SchoolSelectionStep
                        selectedSchool={formData.school}
                        onNext={handleNext}
                        updateFormData={updateFormData}
                    />
                );
            case 1:
                return (
                    <TimePickerStep
                        school={formData.school}
                        selectedDate={formData.tourDate}
                        selectedTime={formData.tourTime}
                        onNext={handleNext}
                        onBack={handleBack}
                        updateFormData={updateFormData}
                    />
                );
            case 2:
                return (
                    <ContactInfoStep
                        formData={formData}
                        onNext={handleNext}
                        onBack={handleBack}
                        updateFormData={updateFormData}
                    />
                );
            case 3:
                return <ConfirmationStep formData={formData} />;
            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '900px' }}>
            {/* Mobile stepper - only show current and next step */}
            <Stepper
                activeStep={0}
                sx={{
                    mb: 4,
                    display: { xs: 'flex', md: 'none' }
                }}
            >
                {steps.slice(activeStep, activeStep + 2).map((label, index) => {
                    const stepNumber = activeStep + index + 1;
                    return (
                        <Step key={`${stepNumber}-${label}`} active={index === 0} completed={false}>
                            <StepLabel
                                StepIconProps={{
                                    icon: stepNumber
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            {/* Desktop stepper - show all steps */}
            <Stepper
                activeStep={activeStep}
                sx={{
                    mb: 4,
                    display: { xs: 'none', md: 'flex' }
                }}
            >
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            <Box sx={{ mt: 4 }}>{renderStepContent()}</Box>
        </Box>
    );
}

export default ScheduleTourFlow;
